import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Incident() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");

  const role = localStorage.getItem("role");
  const adminToken = localStorage.getItem("adminToken");
  const userToken = localStorage.getItem("userToken");
  const token = role === "admin" ? adminToken : userToken;

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/duty/accidentall`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIncidents(res.data || []);
    } catch (error) {
      console.error("Fetch Incident Error:", error);
      toast.error("Failed to load incident requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIncidents();
  }, []);

  const handleStatusUpdate = async (item, action) => {
    try {
      const finalStatus =
        role === "admin"
          ? action === "accept"
            ? "admin_accepted"
            : "admin_rejected"
          : action === "accept"
            ? "manager_accepted"
            : "manager_rejected";

      const Accidentreq = {
        id: item.id,
        req: finalStatus,
        request: "Incident",
      };

      await axios.post(`${BASE_URL}/api/duty/accidentupdate`, Accidentreq, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setIncidents((prev) =>
        prev.map((inc) =>
          inc.id === item.id ? { ...inc, req: finalStatus } : inc
        )
      );

      toast.success(`Request ${action.toUpperCase()}ED Successfully`);
    } catch (error) {
      console.error("Update Error:", error);
      toast.error("Failed to update request");
    }
  };

  const filteredData = incidents.filter((item) => {
    const text = searchText.trim().toLowerCase();
    const searchMatch = text
      ? [
          item.guardData?.id,
          item.guardData?.name,
          item.req,
          item.message,
        ].some((field) =>
          String(field || "").toLowerCase().includes(text)
        )
      : true;

    const statusMatch = selectedStatus
      ? item.req?.includes(selectedStatus)
      : true;

    return searchMatch && statusMatch;
  });

  const getStatusDisplay = (req) => {
    if (req?.includes("accepted")) return { text: "Accepted", class: "active" };
    if (req?.includes("rejected")) return { text: "Rejected", class: "rejected" };
    return { text: "Pending", class: "pending" };
  };

  return (
    <>
      <div className="incident-wrapper">
        {/* Header */}
        <header className="page-header">
          <h1 className="page-title">Incident Request Management</h1>
          <div className="total-card">
            <span className="total-count">{filteredData.length}</span>
            <span className="total-label">Total Requests</span>
          </div>
        </header>

        {/* Filters */}
        <section className="filters-section">
          <div className="filter-group search-group">
            <label>Search</label>
            <div className="search-input-wrapper">
              <i className="fas fa-search"></i>
              <input
                type="text"
                placeholder="Officer ID, name, message..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-group">
            <label>Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <option value="">All Status</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="Arise">Pending</option>
            </select>
          </div>
        </section>

        {/* Table / Cards */}
        <section className="table-section">
          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading incident requests...</p>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-exclamation-triangle"></i>
              <h3>No Incident Requests Found</h3>
              <p>All incidents have been processed or none are pending.</p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="table-container desktop-only">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Officer ID</th>
                      <th>Officer Name</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Request Time</th>
                      <th>Response Time</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((item, index) => {
                      const statusInfo = getStatusDisplay(item.req);
                      const isPending = item.req === "Arise";

                      return (
                        <tr key={item.id} className="table-row">
                          <td>{index + 1}</td>
                          <td>{item.guardData?.id || "—"}</td>
                          <td className="name-cell">{item.guardData?.name || "—"}</td>
                          <td>{item.message || "No message"}</td>
                          <td>
                            <span className={`status-badge ${statusInfo.class}`}>
                              {statusInfo.text}
                            </span>
                          </td>
                          <td>
                            {item.requestTime
                              ? new Date(item.requestTime.split(".")[0]).toLocaleString("en-IN")
                              : "—"}
                          </td>
                          <td>
                            {item.responseTime
                              ? new Date(item.responseTime.split(".")[0]).toLocaleString("en-IN")
                              : "—"}
                          </td>
                          <td className="actions-cell">
                            {isPending ? (
                              <>
                                <button
                                  className="action-btn approve"
                                  onClick={() => handleStatusUpdate(item, "accept")}
                                >
                                  <i className="fas fa-check"></i> Accept
                                </button>
                                <button
                                  className="action-btn reject"
                                  onClick={() => handleStatusUpdate(item, "reject")}
                                >
                                  <i className="fas fa-times"></i> Reject
                                </button>
                              </>
                            ) : (
                              <span className="completed-text">Completed</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile Cards */}
              <div className="mobile-cards mobile-only">
                {filteredData.map((item, index) => {
                  const statusInfo = getStatusDisplay(item.req);
                  const isPending = item.req === "Arise";

                  return (
                    <div key={item.id} className="incident-card">
                      <div className="card-header">
                        <span className="index">#{index + 1}</span>
                        <span className={`status-badge ${statusInfo.class}`}>
                          {statusInfo.text}
                        </span>
                      </div>
                      <h3 className="officer-name">{item.guardData?.name || "Unknown Officer"}</h3>
                      <div className="card-details">
                        <div><strong>Officer ID:</strong> {item.guardData?.id || "—"}</div>
                        <div><strong>Message:</strong> {item.message || "No message"}</div>
                        <div><strong>Request:</strong> {item.requestTime ? new Date(item.requestTime.split(".")[0]).toLocaleString("en-IN") : "—"}</div>
                        <div><strong>Response:</strong> {item.responseTime ? new Date(item.responseTime.split(".")[0]).toLocaleString("en-IN") : "—"}</div>
                      </div>
                      {isPending && (
                        <div className="card-actions">
                          <button
                            className="action-btn approve"
                            onClick={() => handleStatusUpdate(item, "accept")}
                          >
                            <i className="fas fa-check"></i> Accept
                          </button>
                          <button
                            className="action-btn reject"
                            onClick={() => handleStatusUpdate(item, "reject")}
                          >
                            <i className="fas fa-times"></i> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </section>
      </div>

      <style jsx>{`
        .incident-wrapper {
          min-height: 100vh;
          background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
          color: #e2e8f0;
          padding: 20px;
        }

        .page-header {
          margin-bottom: 30px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 700;
          color: #60a5fa;
        }

        .total-card {
          background: rgba(59, 130, 246, 0.2);
          border: 1px solid rgba(59, 130, 246, 0.4);
          padding: 16px 24px;
          border-radius: 16px;
          text-align: center;
        }

        .total-count {
          display: block;
          font-size: 1.8rem;
          font-weight: 800;
          color: #60a5fa;
        }

        .total-label {
          font-size: 0.95rem;
          opacity: 0.9;
        }

        .filters-section {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 30px;
        }

        .filter-group {
          flex: 1;
          min-width: 250px;
          display: flex;
          flex-direction: column;
        }

        .filter-group label {
          font-weight: 600;
          margin-bottom: 8px;
          color: white;
        }

        .search-input-wrapper {
          position: relative;
        }

        .search-input-wrapper i {
          position: absolute;
          left: 18px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .search-input-wrapper input,
        .filter-group select {
          width: 100%;
          padding: 16px 20px;
          padding-left: 52px;
          background: rgba(15, 23, 42, 0.6);
          border: 2px solid rgba(59, 130, 246, 0.4);
          border-radius: 16px;
          color: white;
          font-size: 1rem;
        }

        .filter-group select {
          padding-left: 20px;
        }

        .search-input-wrapper input:focus,
        .filter-group select:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.3);
        }

        .table-section {
          background: rgba(30, 41, 59, 0.8);
          backdrop-filter: blur(12px);
          border-radius: 20px;
          padding: 24px;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 60px 20px;
          color: #94a3b8;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #1e293b;
          border-top: 4px solid #60a5fa;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .empty-state i {
          font-size: 4rem;
          opacity: 0.4;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          margin-bottom: 10px;
          color: white;
        }

        /* Desktop Table */
        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: rgba(59, 130, 246, 0.15);
          padding: 16px;
          text-align: left;
          font-weight: 600;
          color: #60a5fa;
          font-size: 0.95rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table-row {
          transition: all 0.3s ease;
        }

        .table-row:hover {
          background: rgba(59, 130, 246, 0.1);
        }

        .table-row td {
          padding: 18px 16px;
          border-bottom: 1px solid rgba(100, 116, 139, 0.3);
        }

        .name-cell {
          font-weight: 600;
          color: white;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 20px;
          font-weight: 600;
          font-size: 0.9rem;
        }

        .status-badge.active {
          background: rgba(16, 185, 129, 0.2);
          color: #10b981;
        }

        .status-badge.rejected {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .status-badge.pending {
          background: rgba(255, 193, 7, 0.2);
          color: #f59e0b;
        }

        .actions-cell {
          display: flex;
          gap: 12px;
          justify-content: center;
        }

        .action-btn {
          padding: 10px 16px;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .action-btn.approve {
          background: #10b981;
          color: white;
        }

        .action-btn.approve:hover {
          background: #059669;
          transform: translateY(-2px);
        }

        .action-btn.reject {
          background: #ef4444;
          color: white;
        }

        .action-btn.reject:hover {
          background: #dc2626;
          transform: translateY(-2px);
        }

        .completed-text {
          color: #94a3b8;
          font-style: italic;
        }

        /* Mobile Cards */
        .mobile-cards {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .incident-card {
          background: rgba(15, 23, 42, 0.6);
          border: 1px solid rgba(59, 130, 246, 0.3);
          border-radius: 16px;
          padding: 20px;
          transition: all 0.3s ease;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .index {
          font-weight: 600;
          color: #60a5fa;
        }

        .officer-name {
          font-size: 1.3rem;
          font-weight: 700;
          color: white;
          margin-bottom: 12px;
        }

        .card-details {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-bottom: 16px;
          font-size: 0.95rem;
        }

        .card-details div strong {
          color: #60a5fa;
        }

        .card-actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .desktop-only { display: none; }
          .mobile-only { display: flex; }
          .card-details { grid-template-columns: 1fr; }
        }

        @media (min-width: 992px) {
          .mobile-only { display: none; }
          .desktop-only { display: block; }
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .filters-section {
            flex-direction: column;
          }

          .page-title {
            font-size: 1.8rem;
          }
        }
      `}</style>
    </>
  );
}



// //---------------------------------------------3------------------------------------------------

// import React, { useEffect, useState } from "react";
// import axios from "axios";
// import { toast } from "react-toastify";

// const BASE_URL = process.env.REACT_APP_BASE_URL;

// export default function Incident() {
//   const [incidents, setIncidents] = useState([]);
//   const [loading, setLoading] = useState(false);

//   const [searchText, setSearchText] = useState("");
//   const [selectedStatus, setSelectedStatus] = useState("");

//   const role = localStorage.getItem("role");
//   const adminToken = localStorage.getItem("adminToken");
//   const userToken = localStorage.getItem("userToken");
//   const token = role === "admin" ? adminToken : userToken;

//   const fetchIncidents = async () => {
//     try {
//       setLoading(true);

//       const res = await axios.get(`${BASE_URL}/api/duty/accidentall`, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       setIncidents(res.data || []);
//     } catch (error) {
//       console.error("Fetch Incident Error:", error);
//       toast.error("Failed to load incident requests");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchIncidents();
//   }, []);

//   const handleStatusUpdate = async (item, action) => {
//     try {
//       const finalStatus =
//         role === "admin"
//           ? action === "accept"
//             ? "admin_accepted"
//             : "admin_rejected"
//           : action === "accept"
//             ? "manager_accepted"
//             : "manager_rejected";

//       const Accidentreq = {
//         id: item.id,
//         req: finalStatus,
//         request: "Incident",
//       };

//       await axios.post(`${BASE_URL}/api/duty/accidentupdate`, Accidentreq, {
//         headers: { Authorization: `Bearer ${token}` },
//       });

//       setIncidents((prev) =>
//         prev.map((inc) =>
//           inc.id === item.id ? { ...inc, req: finalStatus } : inc
//         )
//       );

//       toast.success(`Request ${action.toUpperCase()}ED Successfully`);
//     } catch (error) {
//       console.error("Update Error:", error);
//       toast.error("Failed to update request");
//     }
//   };

//   const filteredData = incidents.filter((item) => {
//     const text = searchText.trim().toLowerCase();

//     const searchMatch = text
//       ? [
//         item.guardData?.id,
//         item.guardData?.name,
//         item.req,
//         item.message,
//       ].some((field) =>
//         String(field || "").toLowerCase().includes(text)
//       )
//       : true;

//     const statusMatch = selectedStatus
//       ? item.req?.includes(selectedStatus)
//       : true;

//     return searchMatch && statusMatch;
//   });

//   return (
//     <div style={styles.container}>
//       <h2 style={styles.title}>Incident Request Management</h2>

//       <div style={styles.filterRow}>
//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Search</label>
//           <input
//             type="text"
//             value={searchText}
//             onChange={(e) => setSearchText(e.target.value)}
//             placeholder="Search by officer, message, type..."
//             style={styles.input}
//           />
//         </div>

//         <div style={styles.filterCard}>
//           <label style={styles.filterLabel}>Status</label>
//           <select
//             value={selectedStatus}
//             onChange={(e) => setSelectedStatus(e.target.value)}
//             style={styles.select}
//           >
//             <option value="">All</option>
//             <option value="accepted">Accepted</option>
//             <option value="rejected">Rejected</option>
//             <option value="pending">Pending</option>
//           </select>
//         </div>
//       </div>

//       <div style={styles.card}>
//         {loading ? (
//           <div style={styles.noData}>Loading...</div>
//         ) : filteredData.length === 0 ? (
//           <div style={styles.noData}>No Incident Requests Found</div>
//         ) : (
//           <div style={styles.tableWrapper}>
//             <table style={styles.table}>
//               <thead>
//                 <tr>
//                   <th style={styles.th}>#</th>
//                   <th style={styles.th}>Officer ID</th>
//                   <th style={styles.th}>Officer Name</th>
//                   <th style={styles.th}>Message</th>
//                   <th style={styles.th}>Status</th>
//                   <th style={styles.th}>Request Time</th>
//                   <th style={styles.th}>Response Time</th>
//                   {(role === "admin" || role === "user") && (
//                     <th style={styles.th}>Action</th>
//                   )}
//                 </tr>
//               </thead>

//               <tbody>
//                 {filteredData.map((item, index) => {
//                   const statusStyle =
//                     item.req?.includes("accepted")
//                       ? styles.statusActive
//                       : item.req?.includes("rejected")
//                         ? styles.statusInactive
//                         : styles.statusOther;

//                   return (
//                     <tr key={item.id} style={styles.row}>
//                       <td style={styles.td}>{index + 1}</td>
//                       <td style={styles.td}>{item.guardData?.id}</td>
//                       <td style={styles.td}>
//                         {item.guardData?.name || "N/A"}
//                       </td>

//                       <td style={styles.td}>{item.message}</td>

//                       <td style={styles.td}>
//                         <span style={statusStyle}>
//                           {item.req?.includes("accepted")
//                             ? "Accepted"
//                             : item.req?.includes("rejected")
//                               ? "Rejected"
//                               : "Pending"}
//                         </span>
//                       </td>

//                       <td style={styles.td}>
//                         {item.requestTime
//                           ? new Date(
//                             item.requestTime.split(".")[0]
//                           ).toLocaleString("en-IN")
//                           : "N/A"}
//                       </td>

//                       <td style={styles.td}>
//                         {item.responseTime
//                           ? new Date(
//                             item.responseTime.split(".")[0]
//                           ).toLocaleString("en-IN")
//                           : "N/A"}
//                       </td>

//                       {(role === "admin" || role === "user") && (
//                         <td style={styles.td}>
//                           {item.req === "Arise" ? (
//                             <div
//                               style={{
//                                 display: "flex",
//                                 gap: 10,
//                                 justifyContent: "center",
//                                 flexWrap: "wrap",
//                               }}
//                             >
//                               <button
//                                 style={styles.acceptBtn}
//                                 onClick={() =>
//                                   handleStatusUpdate(item, "accept")
//                                 }
//                               >
//                                 Accept
//                               </button>

//                               <button
//                                 style={styles.rejectBtn}
//                                 onClick={() =>
//                                   handleStatusUpdate(item, "reject")
//                                 }
//                               >
//                                 Reject
//                               </button>
//                             </div>
//                           ) : (
//                             <span style={{ color: "#888" }}>Completed</span>
//                           )}
//                         </td>
//                       )}
//                     </tr>
//                   );
//                 })}
//               </tbody>
//             </table>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// /*  RESPONSIVE STYLES ONLY  */
// const styles = {
//   container: { padding: 30, background: "linear-gradient(135deg, rgba(145,195,243,0.7), rgba(233,239,125,0.9))", maxWidth: "100%" },

//   title: {
//     fontSize: "clamp(20px, 4vw, 30px)",
//     fontWeight: 800,
//     marginBottom: 25,
//     color: "#1967d2",
//     // textAlign: "center",
//   },

//   filterRow: {
//     display: "flex",
//     gap: 20,
//     marginBottom: 25,
//     flexWrap: "wrap",
//   },

//   filterCard: {
//     flex: "1 1 280px",
//     padding: 18,
//     borderRadius: 12,
//     background: "rgba(255,255,255,0.45)",
//     boxShadow: "0 3px 12px rgba(0,0,0,0.08)",
//   },

//   filterLabel: { fontSize: 15, fontWeight: 600, marginBottom: 6 },

//   select: {
//     width: "100%",
//     padding: "12px",
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//   },

//   input: {
//     width: "100%",
//     padding: 12,
//     borderRadius: 8,
//     border: "1.5px solid #1a73e8",
//     fontSize: 15,
//     outline: "none",
//   },

//   card: {
//     background: "rgba(255,255,255,0.7)",
//     padding: 10,
//     borderRadius: 15,
//     boxShadow: "0 6px 25px rgba(0,0,0,0.1)",
//     overflow: "hidden",
//   },

//   tableWrapper: { overflowX: "auto", width: "100%" },

//   table: { width: "100%", minWidth: 900, borderCollapse: "collapse" },

//   th: {
//    background: "#b8b8f3ff",
//     padding: "14px 10px",
//     textAlign: "left",
//     borderBottom: "2px solid #ddd",
//     fontWeight: 700,
//     whiteSpace: "nowrap",
//   },

//   row: { height: 62, transition: "0.25s" },

//   td: {
//     padding: "12px 10px",
//     borderBottom: "1px solid #eee",
//     whiteSpace: "nowrap",
//   },

//   statusActive: {
//     background: "#e6ffe6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#2e7d32",
//     fontWeight: 600,
//   },

//   statusInactive: {
//     background: "#ffe6e6",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#d32f2f",
//     fontWeight: 600,
//   },

//   statusOther: {
//     background: "#fff8e1",
//     padding: "6px 12px",
//     borderRadius: 20,
//     color: "#ff9800",
//     fontWeight: 600,
//   },

//   acceptBtn: {
//     background: "#2e7d32",
//     color: "#fff",
//     border: "none",
//     padding: "7px 14px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   rejectBtn: {
//     background: "#d32f2f",
//     color: "#fff",
//     border: "none",
//     padding: "7px 14px",
//     borderRadius: 8,
//     cursor: "pointer",
//   },

//   noData: { textAlign: "center", padding: 40, fontSize: 18, color: "#777" },
// };
